import { Injectable, signal } from '@angular/core';
import { Observable, delay, of } from 'rxjs';
import type { Project, ProjectListResponse } from '../../features/projects/models/project.model';
import type { Task, TaskListResponse } from '../../features/tasks/models/task.model';
import type { User, UserListResponse } from '../../features/users/models/user.model';
import type { LoginResponse } from '../../features/auth/models/auth.model';
import { environment } from '../../../environments/environment';
import { MOCK_LOGIN, mockProjects, mockTasks, mockUsers } from './mock-data';

/** Simule le backend en mémoire (mode démo). Aucune requête HTTP n'est émise. */
@Injectable({ providedIn: 'root' })
export class MockBackend {
  private readonly projects = signal<Project[]>(
    mockProjects.map((project) => ({ ...project })),
  );
  private readonly tasks = signal<Task[]>(mockTasks.map((task) => ({ ...task })));
  private readonly users = signal<User[]>(mockUsers.map((user) => ({ ...user })));

  private nextId = 1000;

  get isMock(): boolean {
    return environment.mock;
  }

  // ---------------------------------------------------------------- Projets

  loadProjects(): Observable<ProjectListResponse> {
    return this.respond({ result: [...this.projects()] });
  }

  saveProject(project: Project): Observable<ProjectListResponse> {
    const now = new Date().toISOString();
    const list = [...this.projects()];
    const index = list.findIndex((item) => item.id === project.id);

    if (index >= 0) {
      list[index] = { ...list[index], ...project, updatedAt: now };
    } else {
      list.unshift({
        ...project,
        id: project.id ?? `p-${this.nextId++}`,
        createdAt: now,
        updatedAt: now,
      });
    }
    this.projects.set(list);
    return this.respond({ result: true });
  }

  removeProject(id: string): Observable<unknown> {
    this.projects.set(this.projects().filter((project) => project.id !== id));
    this.tasks.set(this.tasks().filter((task) => task.projectId !== id));
    return this.respond({ result: true });
  }

  // ---------------------------------------------------------------- Tâches

  loadTasks(): Observable<TaskListResponse> {
    return this.respond({ result: [...this.tasks()] });
  }

  saveTask(task: Task): Observable<TaskListResponse> {
    const now = new Date().toISOString();
    const list = [...this.tasks()];
    const index = list.findIndex((item) => item.id === task.id);

    if (index >= 0) {
      list[index] = { ...list[index], ...task, updatedAt: now };
    } else {
      list.unshift({
        ...task,
        id: task.id ?? `t-${this.nextId++}`,
        notes: task.notes ?? [],
        createdAt: now,
        updatedAt: now,
      });
    }
    this.tasks.set(list);
    return this.respond({ result: true });
  }

  removeTask(id: string): Observable<unknown> {
    this.tasks.set(this.tasks().filter((task) => task.id !== id));
    return this.respond({ result: true });
  }

  // ---------------------------------------------------------------- Utilisateurs

  loadUsers(): Observable<UserListResponse> {
    return this.respond({ result: [...this.users()] });
  }

  me(): Observable<UserListResponse> {
    const current = this.users().find((user) => user.login === MOCK_LOGIN);
    return this.respond({ result: current ? [current] : [] });
  }

  // ---------------------------------------------------------------- Auth

  login(): Observable<LoginResponse> {
    return this.respond({
      tokenResponse: { token: MOCK_TOKEN },
      roles: ['Admin'],
      profiles: ['ADMINISTRATEUR'],
      permissions: [],
    });
  }

  /** Réponse différée pour simuler la latence réseau. */
  private respond<T>(body: T): Observable<T> {
    return of(body).pipe(delay(300));
  }
}

/** Token factice au format JWT (subject = admin) pour le mode démo uniquement. */
function base64(value: object): string {
  // Payload ASCII uniquement : btoa est limité au Latin-1.
  return btoa(JSON.stringify(value)).replace(/=+$/, '');
}

export const MOCK_TOKEN = `${base64({ alg: 'none', typ: 'JWT' })}.${base64({
  sub: MOCK_LOGIN,
  name: 'Administrateur',
})}.signature-demo`;
